import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sparkles,
  Link2,
  FileText,
  Image as ImageIcon,
  X,
  Loader2,
  Upload,
  Plus,
  Code,
  Globe,
  BarChart3,
  Presentation,
  Gamepad2,
} from "lucide-react";
import { builderApi } from "@/lib/api/builder";
import { apiClient, createAuthHeaders } from "@/lib/api-client";
import { editorAPI } from "@/lib/editor/api";
import { getTemplateIdForTags } from "@/lib/templates";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { getActiveWorkspaceId, useWorkspaceStore } from "@/stores/workspace-store";
import { createFileUploader } from "@/lib/files/uppy-upload";
import { validateFilesForUpload } from "@/lib/files/image-validation";
import toast from "react-hot-toast";
import { ACCEPT_ATTRIBUTE as ACCEPTED_FILE_TYPES } from "@optidev/shared-types";
import { consumePendingFiles } from "@/embed/pending-files";
import { useEmbedSubscriptionGate } from "@/embed/useEmbedSubscriptionGate";
import { EmbedSubscriptionBlockedModal } from "@/embed/EmbedSubscriptionBlockedModal";
import type { AppTag } from "@optidev/shared-types";
import type { LucideIcon } from "lucide-react";

type BuilderMode = "plan" | "execute";

interface ExamplePrompt {
  title: string;
  content: string;
}

interface SubCategory {
  id: string;
  label: string;
  prompt: string;
}

interface ProjectCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  tag: AppTag;
  subcategories: SubCategory[];
  examples: ExamplePrompt[];
}

const PROJECT_CATEGORIES: ProjectCategory[] = [
  {
    id: "website",
    label: "Website",
    icon: Globe,
    tag: "landing-page",
    subcategories: [
      { id: "landing-page", label: "Landing Page", prompt: "A modern landing page with a hero section, features grid, testimonials, and a call-to-action" },
      { id: "saas", label: "SaaS", prompt: "A SaaS landing page with pricing tiers, feature comparison, testimonials carousel, and signup form" },
      { id: "portfolio", label: "Portfolio", prompt: "A portfolio website to showcase projects with a filterable gallery, about section, and contact form" },
      { id: "e-commerce", label: "E-commerce", prompt: "An e-commerce storefront with product cards, category filters, shopping cart, and checkout flow" },
      { id: "blog", label: "Blog", prompt: "A blog website with article cards, category navigation, search, and a reading view with related posts" },
      { id: "restaurant", label: "Restaurant", prompt: "A restaurant website with an interactive menu, photo gallery, reservations form, and location map" },
    ],
    examples: [
      { title: "A restaurant site with menu and online ordering", content: "A restaurant website with an interactive menu showing food categories, item cards with photos and prices, and a simple order summary sidebar" },
      { title: "SaaS landing page with pricing tiers and signup", content: "A modern SaaS landing page with a hero section, feature comparison grid, three-tier pricing table, testimonials carousel, and a contact form" },
      { title: "Portfolio site to showcase my design work", content: "A portfolio website with a filterable project gallery, case study pages, about section, and contact form" },
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: BarChart3,
    tag: "dashboard",
    subcategories: [
      { id: "sales-analytics", label: "Sales Analytics", prompt: "A sales analytics dashboard with revenue line charts, top deals table, conversion funnel, and KPI cards showing monthly trends" },
      { id: "iot-monitoring", label: "IoT Monitoring", prompt: "An IoT sensor monitoring dashboard with real-time gauge widgets, temperature and humidity charts, alert history log, and device status grid" },
      { id: "project-board", label: "Project Board", prompt: "A project management board with drag-and-drop task columns, priority filters, team member avatars, and a progress overview sidebar" },
      { id: "kpi-tracker", label: "KPI Tracker", prompt: "A KPI tracking dashboard with goal progress bars, metric cards, trend sparklines, and a team performance leaderboard" },
    ],
    examples: [
      { title: "Turn my ugly spreadsheet into a polished dashboard", content: "A polished analytics dashboard that visualizes spreadsheet data with clean charts, summary cards, and filterable tables" },
      { title: "IoT sensor panel with real-time gauges and alerts", content: "An IoT sensor monitoring dashboard with real-time gauge widgets, temperature and humidity charts, alert history log, and device status grid" },
      { title: "Project board with drag-and-drop task columns", content: "A project management board with drag-and-drop task columns, priority filters, team member avatars, and a progress overview sidebar" },
    ],
  },
  {
    id: "slides",
    label: "Slides",
    icon: Presentation,
    tag: "presentation",
    subcategories: [
      { id: "pitch-deck", label: "Pitch Deck", prompt: "A startup pitch deck with slides for the problem, solution, market size, traction metrics, team bios, and a closing call-to-action" },
      { id: "business-review", label: "Business Review", prompt: "A quarterly business review presentation with KPI summary cards, revenue and growth charts, key highlights, challenges, and goals for next quarter" },
      { id: "company-overview", label: "Company Overview", prompt: "A company overview deck with slides for mission and vision, core services, team structure, client testimonials, and contact information" },
      { id: "product-demo", label: "Product Demo", prompt: "A product demo presentation with feature walkthrough slides, comparison charts, live demo screenshots, and a pricing summary" },
    ],
    examples: [
      { title: "Startup pitch deck for our Series A", content: "A startup pitch deck presentation with slides for the problem, solution, market size, traction metrics, team bios, and a call-to-action closing slide" },
      { title: "Quarterly business review with charts", content: "A quarterly business review presentation with KPI summary cards, revenue and growth charts, key highlights, challenges faced, and goals for next quarter" },
      { title: "Company overview for new hires", content: "A company overview deck with slides for mission and vision, core services, team structure, client testimonials, and contact information" },
    ],
  },
  {
    id: "game",
    label: "Game",
    icon: Gamepad2,
    tag: "game",
    subcategories: [
      { id: "quiz", label: "Quiz", prompt: "A trivia quiz game with multiple-choice questions, score tracking, timer, and a results leaderboard" },
      { id: "trivia", label: "Trivia", prompt: "A trivia game show experience with category selection, difficulty levels, streaks, and animated score reveals" },
      { id: "arcade", label: "Arcade", prompt: "A classic arcade game like snake or breakout with keyboard controls, score counter, lives, and increasing difficulty" },
      { id: "puzzle", label: "Puzzle", prompt: "An interactive puzzle game with multiple levels, hints system, move counter, and completion animations" },
    ],
    examples: [
      { title: "A trivia quiz game with leaderboards", content: "A trivia quiz game with multiple-choice questions, score tracking, timer, streak bonuses, and a results leaderboard" },
      { title: "Simple arcade game like snake or breakout", content: "A classic arcade game like snake with keyboard controls, score counter, lives system, and increasing difficulty levels" },
      { title: "Interactive puzzle game with levels", content: "An interactive puzzle game with multiple levels, drag-and-drop mechanics, hints system, move counter, and completion animations" },
    ],
  },
];


interface UploadedFile {
  file: File;
  fileId: string;
  refId: string;
  downloadUrl: string;
  thumbnailUrl?: string;
  uploadState: "uploading" | "completed" | "failed";
  uploadProgress?: number;
  error?: string;
}

interface ReferenceUrl {
  url: string;
  id: string;
}

interface RecentProject {
  id: string;
  name: string;
  updatedAt: string;
  thumbnailUrl?: string;
  mode?: "lite" | "power";
  workspaceId: string;
}

export default function Dashboard() {
  const navigate = useNavigate();

  const { getIdToken } = useAuth();
  const { canCreateProject } = usePermissions();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const { isBlocked: isEmbedBlocked } = useEmbedSubscriptionGate();
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<BuilderMode>("execute");
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [pendingUploadsInProgress, setPendingUploadsInProgress] =
    useState(false);

  // Attachments
  const [referenceUrls, setReferenceUrls] = useState<ReferenceUrl[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // UI states
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState("");
  const [urlError, setUrlError] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Category selection
  const [selectedCategory, setSelectedCategory] =
    useState<ProjectCategory | null>(null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  // Recent projects
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [connectorIcons, setConnectorIcons] = useState<{ iconUrl: string; name: string }[]>([]);
  const [totalConnectors, setTotalConnectors] = useState(0);

  // Auto-resize textarea as content grows
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, 400);
    textarea.style.height = `${newHeight}px`;
  }, [prompt]);

  // Decode prompt from URL params (embed provisioning or marketing website handover).
  // Read from window.location.search (not useSearchParams) because navigate() updates
  // the browser URL synchronously, but React Router's context may not have re-rendered
  // yet when a Zustand update (setNeedsProvisioning) triggers the mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encodedPrompt = params.get("prompt");
    const autoSubmit = params.get("autoSubmit");

    if (encodedPrompt) {
      try {
        const decodedPrompt = decodeURIComponent(atob(encodedPrompt));
        setPrompt(decodedPrompt);

        // Clear prompt/autoSubmit from URL (keep other params like embed, ws)
        // Use history.replaceState (not setSearchParams) because in embed mode
        // React Router context may not have re-rendered yet when Zustand triggers mount.
        params.delete("prompt");
        params.delete("autoSubmit");
        const cleanUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", cleanUrl);

        if (autoSubmit === "true") {
          setShouldAutoSubmit(true);
        }
      } catch (error) {
        console.error("Failed to decode prompt from URL:", error);
      }
    }
  }, []); // Run once on mount

  // Auto-submit when prompt is set from URL and autoSubmit flag is true.
  // Waits for any pending file uploads (from EmbedOnboarding) to finish first.
  useEffect(() => {
    if (
      shouldAutoSubmit &&
      prompt.trim() &&
      !isLoading &&
      !pendingUploadsInProgress
    ) {
      const timer = setTimeout(() => {
        handleSubmit();
        setShouldAutoSubmit(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [shouldAutoSubmit, prompt, isLoading, pendingUploadsInProgress]);

  // Fetch recent projects on mount and when workspace changes
  useEffect(() => {
    const fetchRecentProjects = async () => {
      setLoadingProjects(true);
      try {
        // Use dedicated recent projects endpoint (database-level sorting/limiting)
        const projects = await editorAPI.getRecentProjects(5);
        setRecentProjects(projects);
      } catch (error) {
        console.error("Failed to fetch recent projects:", error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchRecentProjects();

    // Fetch connector icons
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const { data } = await apiClient.get<{ id: string; name: string; iconUrl?: string; status: string }[]>(
          "/api/connectors",
          { headers: createAuthHeaders(token) },
        );
        const available = data.filter((c) => c.status === "available" && c.iconUrl);
        const featured = ["stripe", "excel", "databricks", "snowflake", "postgresql"];
        const picked = featured
          .map((id) => available.find((c) => c.id === id))
          .filter(Boolean) as typeof available;
        setConnectorIcons(picked.map((c) => ({ iconUrl: c.iconUrl!, name: c.name })));
        setTotalConnectors(available.length);
      } catch { /* silent */ }
    })();
  }, [activeWorkspaceId]);

  // Helper to format date
  const formatDate = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  // Reference URL handlers
  const handleAddUrl = () => {
    let trimmedUrl = urlInputValue.trim();
    if (!trimmedUrl) return;

    if (trimmedUrl.includes("\\") || /\s/.test(trimmedUrl)) {
      setUrlError("Invalid characters in URL");
      return;
    }

    if (!trimmedUrl.match(/^https?:\/\//i)) {
      trimmedUrl = "https://" + trimmedUrl;
    }

    const domainPattern =
      /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]+)?(\/.*)?$/;

    if (!domainPattern.test(trimmedUrl)) {
      setUrlError("Invalid URL format (e.g., example.com)");
      return;
    }

    try {
      new URL(trimmedUrl);

      if (referenceUrls.find((r) => r.url === trimmedUrl)) {
        setUrlError("URL already added");
        return;
      }

      setReferenceUrls([
        ...referenceUrls,
        { url: trimmedUrl, id: Date.now().toString() },
      ]);
      setUrlInputValue("");
      setShowUrlInput(false);
      setUrlError("");
      setError("");
    } catch {
      setUrlError("Invalid URL format");
    }
  };

  const handleUrlInputChange = (value: string) => {
    setUrlInputValue(value);
    setUrlError("");
  };

  const handleRemoveUrl = (id: string) => {
    setReferenceUrls(referenceUrls.filter((r) => r.id !== id));
  };

  // File upload handlers
  const uploadFileImmediately = async (file: File): Promise<UploadedFile> => {
    try {
      const uppy = createFileUploader({
        userMessage: prompt,
        source: "builder",
        onProgress: (_file, progress) => {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.file.name === file.name && f.file.size === file.size
                ? { ...f, uploadProgress: progress }
                : f,
            ),
          );
        },
      });

      uppy.addFile({ name: file.name, type: file.type, data: file });

      const result = await new Promise<{
        fileId: string;
        refId: string;
        downloadUrl: string;
        thumbnailUrl?: string;
      }>((resolve, reject) => {
        uppy.on("complete", async (result) => {
          if (result.successful && result.successful.length > 0) {
            const uploadedFile = result.successful[0];
            let attempts = 0;
            while (
              !uploadedFile.meta.uploadCompleteFinished &&
              attempts < 100
            ) {
              await new Promise((r) => setTimeout(r, 100));
              attempts++;
            }
            resolve({
              fileId: uploadedFile.meta.fileId as string,
              refId: uploadedFile.meta.refId as string,
              downloadUrl: uploadedFile.meta.downloadUrl as string,
              thumbnailUrl: uploadedFile.meta.thumbnailUrl as
                | string
                | undefined,
            });
          } else {
            reject(new Error("Upload failed"));
          }
        });
        uppy.on("error", (error) => reject(error));
        uppy.upload();
      });

      return {
        file,
        uploadState: "completed",
        uploadProgress: 100,
        fileId: result.fileId,
        refId: result.refId,
        downloadUrl: result.downloadUrl,
        thumbnailUrl: result.thumbnailUrl,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      console.error("[Dashboard] File upload error:", error);
      return {
        file,
        fileId: "",
        refId: "",
        downloadUrl: "",
        uploadState: "failed",
        error: errorMessage,
      };
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const allFiles = Array.from(files);

    // Validate files before upload (async - checks magic bytes for images)
    const { valid: fileArray, invalid } =
      await validateFilesForUpload(allFiles);

    // Show toast for rejected files
    if (invalid.length > 0) {
      invalid.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`, { duration: 4000 });
      });
    }

    // Only proceed with valid files
    if (fileArray.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const filesWithState: UploadedFile[] = fileArray.map((file) => ({
      file,
      fileId: "",
      refId: "",
      downloadUrl: "",
      uploadState: "uploading" as const,
      uploadProgress: 0,
    }));

    setUploadedFiles((prev) => [...prev, ...filesWithState]);

    const uploadPromises = fileArray.map((file) => uploadFileImmediately(file));
    const uploadedFileResults = await Promise.all(uploadPromises);

    setUploadedFiles((prev) => {
      const filtered = prev.filter(
        (f) =>
          f.uploadState !== "uploading" ||
          !fileArray.some(
            (nf) => nf.name === f.file.name && nf.size === f.file.size,
          ),
      );
      return [...filtered, ...uploadedFileResults];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((files) => files.filter((f) => f.fileId !== fileId));
  };

  // Pick up files stashed by EmbedOnboarding (collected before workspace existed).
  // Feeds them into the same upload pipeline as manually-attached files.
  // Sets pendingUploadsInProgress so auto-submit waits for uploads to finish.
  useEffect(() => {
    const pending = consumePendingFiles();
    if (pending.length === 0) return;

    setPendingUploadsInProgress(true);

    (async () => {
      const { valid, invalid } = await validateFilesForUpload(pending);
      if (invalid.length > 0) {
        invalid.forEach(({ file, reason }) => {
          toast.error(`${file.name}: ${reason}`, { duration: 4000 });
        });
      }
      if (valid.length === 0) {
        setPendingUploadsInProgress(false);
        return;
      }

      const filesWithState: UploadedFile[] = valid.map((file) => ({
        file,
        fileId: "",
        refId: "",
        downloadUrl: "",
        uploadState: "uploading" as const,
        uploadProgress: 0,
      }));

      setUploadedFiles((prev) => [...prev, ...filesWithState]);

      const results = await Promise.all(
        valid.map((file) => uploadFileImmediately(file)),
      );

      setUploadedFiles((prev) => {
        const filtered = prev.filter(
          (f) =>
            f.uploadState !== "uploading" ||
            !valid.some(
              (nf) => nf.name === f.file.name && nf.size === f.file.size,
            ),
        );
        return [...filtered, ...results];
      });

      setPendingUploadsInProgress(false);
    })();
  }, []); // Run once on mount

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const allFiles = Array.from(files);

    // Validate files before upload (async - checks magic bytes for images)
    const { valid: fileArray, invalid } =
      await validateFilesForUpload(allFiles);

    // Show toast for rejected files
    if (invalid.length > 0) {
      invalid.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`, { duration: 4000 });
      });
    }

    // Only proceed with valid files
    if (fileArray.length === 0) {
      return;
    }

    const filesWithState: UploadedFile[] = fileArray.map((file) => ({
      file,
      fileId: "",
      refId: "",
      downloadUrl: "",
      uploadState: "uploading" as const,
      uploadProgress: 0,
    }));

    setUploadedFiles((prev) => [...prev, ...filesWithState]);

    const uploadPromises = fileArray.map((file) => uploadFileImmediately(file));
    const uploadedFileResults = await Promise.all(uploadPromises);

    setUploadedFiles((prev) => {
      const filtered = prev.filter(
        (f) =>
          f.uploadState !== "uploading" ||
          !fileArray.some(
            (nf) => nf.name === f.file.name && nf.size === f.file.size,
          ),
      );
      return [...filtered, ...uploadedFileResults];
    });
  };

  // Check if file is an image
  const isImageFile = (file: File) => {
    return file.type.startsWith("image/");
  };

  // Get file icon based on type
  const getFileIcon = (file: File) => {
    if (isImageFile(file)) {
      return <ImageIcon className="w-5 h-5 text-gray-400" />;
    }
    return <FileText className="w-5 h-5 text-gray-400" />;
  };

  // Submit handler - calls pre-flight API
  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setError("Please describe what you want to build");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required. Please log in.");
        return;
      }

      // Call pre-flight API for slug and classification
      const completedFiles = uploadedFiles.filter(
        (f) => f.uploadState === "completed",
      );
      const result = await builderApi.preFlight(token, {
        prompt,
        uploadedFiles: completedFiles.map((f) => ({
          fileName: f.file.name,
          fileType: f.file.type,
          downloadUrl: f.downloadUrl,
        })),
      });

      // Merge user-selected category tag with AI-classified tags (user choice takes priority)
      const allTags = selectedCategory
        ? [
            selectedCategory.tag,
            ...result.tags.filter((t: AppTag) => t !== selectedCategory.tag),
          ]
        : result.tags;

      // Create project with user-selected mode
      const createResult = await builderApi.complete(token, {
        slug: result.slug,
        builderData: {
          fromBuilder: true,
          initialPrompt: prompt,
          appType: result.app_type,
          tags: allTags,
          mode,
          referenceUrls: referenceUrls.map((r) => r.url),
          uploadedFileIds: completedFiles.map((f) => f.fileId),
          uploadedFiles: completedFiles.map((f) => ({
            fileId: f.fileId,
            refId: f.refId,
            originalName: f.file.name,
            mimeType: f.file.type,
            downloadUrl: f.downloadUrl,
            thumbnailUrl: f.thumbnailUrl,
            fileSize: f.file.size,
          })),
        },
        templateId: getTemplateIdForTags(allTags),
      });

      // Navigate to editor with workspace context
      const wsId = getActiveWorkspaceId();
      navigate(
        `/dashboard/editor/${createResult.projectId}${wsId ? `?ws=${wsId}` : ""}`,
      );
    } catch (err: unknown) {
      console.error("Failed to create project:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create project. Please try again.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isEmbedBlocked) {
    return <EmbedSubscriptionBlockedModal />;
  }

  return (
    <div
      className="absolute inset-0 overflow-y-auto"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hero Section - Shows peek of content below */}
      <div className="min-h-[calc(100vh-220px)] sm:min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-4 pt-4 pb-8 sm:py-20 -mt-2 sm:mt-0">
        <div className="w-full max-w-3xl space-y-4 sm:space-y-8 relative z-10">
          {/* Badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
              <Sparkles className="w-3 h-3 text-purple-500 dark:text-purple-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                Optidev x OptiSigns
              </span>
            </div>
          </div>

          {/* Main Heading */}
          <div className="text-center space-y-3 sm:space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-[-0.02em] text-gray-900 dark:text-white leading-[1.1]">
              Build something{" "}
              <span className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 dark:from-purple-400 dark:via-purple-300 dark:to-indigo-400 bg-clip-text text-transparent">
                great
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-lg mx-auto leading-relaxed">
              Create apps and websites with ease through chat
            </p>
          </div>

          {/* Prompt Input Container */}
          <div className="relative">
            {/* Unified Container with Border */}
            <div
              className={`relative rounded-xl sm:rounded-2xl bg-white dark:bg-card
                       border ${isDragging ? "border-purple-500 border-dashed border-2" : "border-gray-200/70 dark:border-border border-solid"}
                       shadow-none
                       transition-all duration-200`}
            >
              {/* Drop zone overlay inside input */}
              {isDragging && (
                <div className="absolute inset-0 z-50 rounded-2xl bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-blue-600/20 backdrop-blur-md border-2 border-dashed border-purple-400/60 flex items-center justify-center pointer-events-none">
                  <div className="flex items-center gap-3">
                    <Upload
                      className="w-5 h-5 text-purple-200"
                      strokeWidth={2.5}
                    />
                    <p className="text-sm font-medium text-white drop-shadow">
                      Drop files here
                    </p>
                  </div>
                </div>
              )}

              {/* Textarea */}
              <div className="px-4 sm:px-5 pt-4 pb-1">
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      prompt.trim() &&
                      canCreateProject
                    ) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder={
                    canCreateProject
                      ? "Describe what you want to build..."
                      : "You don't have permission to create projects"
                  }
                  disabled={isLoading || !canCreateProject}
                  rows={1}
                  className="w-full text-base bg-transparent text-gray-900 dark:text-gray-100
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none resize-none overflow-y-auto
                         disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ minHeight: "84px", maxHeight: "400px" }}
                />
              </div>

              {/* Bottom toolbar: [+] [attachments] [category chip] [Plan|Execute] ... [Submit] */}
              <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2 border-t border-gray-100 dark:border-border/50">
                {/* Plus button with click dropdown for attach/URL */}
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowPlusMenu(!showPlusMenu)}
                    disabled={!canCreateProject}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Attach files or add reference URL"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {showPlusMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowPlusMenu(false)} />
                      <div className="absolute top-full left-0 mt-1 flex flex-col bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg overflow-hidden z-20 min-w-[160px]">
                        <button
                          type="button"
                          onClick={() => { fileInputRef.current?.click(); setShowPlusMenu(false); }}
                          className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Attach files
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowUrlInput(true); setShowPlusMenu(false); }}
                          className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                          Reference URL
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-x-auto overflow-y-visible scrollbar-hide h-9">
                  {/* File Thumbnails */}
                  {uploadedFiles.map((file, index) => {
                    const isUploading = file.uploadState === "uploading";
                    const isFailed = file.uploadState === "failed";
                    const isImage = isImageFile(file.file);
                    const previewUrl = isImage
                      ? file.thumbnailUrl ||
                        file.downloadUrl ||
                        URL.createObjectURL(file.file)
                      : "";

                    return (
                      <Tooltip key={file.fileId || `uploading-${index}`} delayDuration={200}>
                        <TooltipTrigger asChild>
                          <div
                            className="relative group/thumb flex-shrink-0"
                          >
                            <div className="relative w-8 h-8 bg-gray-100 dark:bg-white/[0.06] rounded-md border border-gray-200 dark:border-white/[0.06]">
                              <div className="absolute inset-0 rounded-md overflow-hidden flex items-center justify-center">
                                {isImage && previewUrl ? (
                                  <img
                                    src={previewUrl}
                                    alt={file.file.name}
                                    className={`w-full h-full object-cover ${isUploading ? "opacity-50" : ""}`}
                                  />
                                ) : (
                                  <div
                                    className="w-full h-full flex items-center justify-center"
                                  >
                                    {getFileIcon(file.file)}
                                  </div>
                                )}

                                {isUploading && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                                  </div>
                                )}

                                {isFailed && (
                                  <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                                    <X className="w-3.5 h-3.5 text-red-500" />
                                  </div>
                                )}
                              </div>

                              {!isUploading && !isFailed && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(file.fileId)}
                                  className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gray-800 hover:bg-red-600 dark:bg-gray-800 dark:hover:bg-red-600
                                         rounded-full flex items-center justify-center opacity-0 group-hover/thumb:opacity-100
                                         transition-all shadow-lg border border-white/30 z-10"
                                  title="Remove"
                                >
                                  <X className="w-3 h-3 text-white" />
                                </button>
                              )}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-card text-foreground border border-border text-xs shadow-md">
                          {file.file.name}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}

                  {/* Reference URL Pills */}
                  {referenceUrls.map((ref) => {
                    const displayUrl = ref.url
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "");
                    return (
                      <div
                        key={ref.id}
                        className="relative group/url flex-shrink-0"
                      >
                        <div className="inline-flex items-center gap-1.5 px-2.5 h-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-xs hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                          <Link2 className="w-3 h-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="max-w-[120px] truncate text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 font-medium"
                            title={ref.url}
                          >
                            {displayUrl}
                          </a>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveUrl(ref.id)}
                          className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gray-800 hover:bg-red-600 dark:bg-gray-800 dark:hover:bg-red-600
                                 rounded-full flex items-center justify-center opacity-0 group-hover/url:opacity-100
                                 transition-all shadow-lg border border-white/30 z-10"
                          title="Remove"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    );
                  })}

                  {/* URL Input */}
                  {showUrlInput && (
                    <div className="flex-shrink-0">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-xs transition-all
                                   ${
                                     urlError
                                       ? "bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-600"
                                       : "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                                   }`}
                      >
                        <Link2
                          className={`w-3 h-3 flex-shrink-0 ${urlError ? "text-red-500 dark:text-red-400" : "text-blue-600 dark:text-blue-400"}`}
                        />
                        <input
                          type="url"
                          value={urlInputValue}
                          onChange={(e) => handleUrlInputChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddUrl();
                            }
                            if (e.key === "Escape") {
                              setShowUrlInput(false);
                              setUrlInputValue("");
                              setUrlError("");
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              if (!urlInputValue.trim()) {
                                setShowUrlInput(false);
                                setUrlError("");
                              }
                            }, 150);
                          }}
                          placeholder="Paste URL"
                          className={`w-[120px] bg-transparent text-xs focus:outline-none font-medium
                                 ${
                                   urlError
                                     ? "text-red-700 dark:text-red-300 placeholder-red-400 dark:placeholder-red-500"
                                     : "text-blue-700 dark:text-blue-300 placeholder-blue-400 dark:placeholder-blue-500"
                                 }`}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={handleAddUrl}
                          disabled={!urlInputValue.trim()}
                          className={`px-1.5 py-0.5 rounded text-xs font-medium transition-all
                                 ${
                                   urlInputValue.trim()
                                     ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                                     : "bg-gray-200 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                 }`}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowUrlInput(false);
                            setUrlInputValue("");
                            setUrlError("");
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Cancel"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Selected Category Chip */}
                  {selectedCategory && (
                    <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-foreground/90 text-background text-sm font-medium flex-shrink-0">
                      <selectedCategory.icon className="w-4 h-4" />
                      {selectedCategory.label}
                      <button
                        type="button"
                        onClick={() => setSelectedCategory(null)}
                        className="ml-0.5 opacity-70 hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}

                </div>

                {/* Right side: Connectors + Plan/Execute + Submit (pinned) */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Connector icons stack */}
                  {connectorIcons.length > 0 && (
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => navigate("/dashboard/connectors")}
                          className="hidden sm:flex items-center -space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          {connectorIcons.map((c) => {
                            const isDark = document.documentElement.classList.contains("dark");
                            const src = isDark && c.iconUrl.includes("-dark.svg")
                              ? c.iconUrl.replace("-dark.svg", "-light.svg")
                              : c.iconUrl;
                            return (
                              <div
                                key={c.name}
                                className="w-7 h-7 rounded-full bg-white dark:bg-card border border-border/30 flex items-center justify-center"
                              >
                                <img src={src} alt={c.name} className="w-3.5 h-3.5" />
                              </div>
                            );
                          })}
                          {totalConnectors > 5 && (
                            <div className="w-7 h-7 rounded-full bg-muted border border-border/30 flex items-center justify-center text-[9px] font-medium text-muted-foreground">
                              +{totalConnectors - 5}
                            </div>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-card text-foreground border border-border text-xs shadow-md">Integrate with connectors</TooltipContent>
                    </Tooltip>
                  )}

                  <div className="flex items-center h-8 p-0.5 bg-gray-100 dark:bg-white/[0.06] rounded-lg">
                    <button
                      onClick={() => setMode("plan")}
                      className={`px-3 h-full text-xs font-medium rounded-md transition-all ${
                        mode === "plan"
                          ? "bg-white dark:bg-white/[0.1] text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      }`}
                    >
                      Plan
                    </button>
                    <button
                      onClick={() => setMode("execute")}
                      className={`px-3 h-full text-xs font-medium rounded-md transition-all ${
                        mode === "execute"
                          ? "bg-white dark:bg-white/[0.1] text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      }`}
                    >
                      Execute
                    </button>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isLoading || !prompt.trim() || !canCreateProject}
                    className="w-8 h-8 rounded-lg bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 active:scale-95
                           disabled:bg-gray-300 dark:disabled:bg-white/[0.06] disabled:cursor-not-allowed
                           flex items-center justify-center transition-all duration-200"
                    aria-label="Start building"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}

            {/* Hidden File Input - Now accepts more file types */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Category / Sub-category Pills (clean swap) */}
            <div className="mt-3">
              {!selectedCategory ? (
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {PROJECT_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category)}
                        className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg border border-gray-300/70 dark:border-border bg-gray-50 dark:bg-card hover:border-gray-300 dark:hover:border-muted-foreground/30 hover:bg-gray-100 dark:hover:bg-muted transition-all duration-200 text-sm font-medium text-gray-700 dark:text-muted-foreground"
                      >
                        <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        {category.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {selectedCategory.subcategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => {
                        setPrompt(sub.prompt);
                        setError("");
                        textareaRef.current?.focus();
                      }}
                      className="px-3.5 py-1.5 rounded-lg border border-gray-300/70 dark:border-border bg-gray-50 dark:bg-card text-sm text-gray-700 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-muted-foreground/40 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-muted transition-all duration-200"
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Projects Section */}
      {!loadingProjects && recentProjects.length > 0 && (
        <div className="pb-8 sm:pb-20">
          <div className="w-full max-w-6xl mx-auto px-4">
            <div className="rounded-xl bg-white dark:bg-card border border-border p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Recent Projects
                </h2>
                <button
                  onClick={() => navigate("/dashboard/editor")}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
                >
                  View all
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {recentProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() =>
                      navigate(
                        `/dashboard/editor/${project.id}${project.mode === "power" ? "/power" : ""}?ws=${project.workspaceId}`,
                      )
                    }
                    className="overflow-hidden rounded-lg bg-card dark:bg-white/[0.04] border border-border dark:border-white/[0.06] hover:border-border/80 dark:hover:border-white/[0.1] shadow-sm dark:shadow-none transition-all text-left group"
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-[16/9] bg-accent dark:bg-white/[0.04] overflow-hidden">
                      {project.thumbnailUrl ? (
                        <img
                          src={project.thumbnailUrl}
                          alt={project.name}
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextElementSibling?.classList.remove(
                              "hidden",
                            );
                          }}
                        />
                      ) : null}
                      <div
                        className={`absolute inset-0 flex items-center justify-center ${project.thumbnailUrl ? "hidden" : ""}`}
                      >
                        <Code className="w-6 h-6 text-gray-400 dark:text-gray-600" />
                      </div>
                    </div>

                    {/* Name + date */}
                    <div className="px-3.5 py-3 flex items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                        {project.name}
                      </h3>
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                        {formatDate(project.updatedAt)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
